import React, { useEffect, useState } from 'react';
import api from '../services/api';

const TIPO_BADGE: Record<string, { label: string; color: string }> = {
  rotura_tuberia: { label: '🔧 Rotura', color: '#ef4444' },
  fuga_agua:      { label: '💧 Fuga',   color: '#3b82f6' },
  sin_agua:       { label: '🚫 Sin agua', color: '#f97316' },
  dano_medidor:   { label: '📊 Medidor', color: '#8b5cf6' },
  otro:           { label: '⚠️ Otro',   color: '#64748b' },
};

const ESTADO_COLOR: Record<string, string> = {
  pendiente:  '#ef4444',
  en_proceso: '#f97316',
  resuelto:   '#22c55e',
};

const Averias: React.FC = () => {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const esTecnico = usuario.rol === 'tecnico';

  const [averias, setAverias]     = useState<any[]>([]);
  const [usuarios, setUsuarios]   = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [asignarModal, setAsignarModal] = useState<{ id: string; titulo: string } | null>(null);
  const [tecnicoSeleccionado, setTecnicoSeleccionado] = useState('');
  const [form, setForm] = useState({
    titulo: '', descripcion: '', tipo: 'otro', clienteId: '',
  });

  const cargar = async () => {
    setLoading(true);
    try {
      const endpoint = esTecnico ? '/averias/mis-averias' : '/averias';
      const [avRes, usRes] = await Promise.all([
        api.get(endpoint),
        esTecnico ? Promise.resolve({ data: [] }) : api.get('/usuarios'),
      ]);
      setAverias(avRes.data);
      setUsuarios(usRes.data.filter((u: any) => u.rol === 'tecnico' && u.activo !== false));
    } catch { /* silenciar */ }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const marcarEnProceso = async (id: string) => {
    try {
      await api.put(`/averias/${id}`, { estado: 'en_proceso', tecnicoAsignadoId: usuario.id });
      cargar();
    } catch { alert('Error al actualizar avería'); }
  };

  const resolverAveria = async (id: string) => {
    const evidencia = window.prompt('Describe cómo se resolvió la avería:');
    if (evidencia === null) return;
    try {
      await api.put(`/averias/${id}/resolver`, { evidencia });
      cargar();
    } catch { alert('Error al resolver avería'); }
  };

  const crear = async () => {
    if (!form.titulo.trim()) { alert('El titulo es obligatorio'); return; }
    try {
      await api.post('/averias', form);
      setModal(false);
      setForm({ titulo: '', descripcion: '', tipo: 'otro', clienteId: '' });
      cargar();
    } catch { alert('Error al crear la averia'); }
  };

  const asignar = async () => {
    if (!asignarModal || !tecnicoSeleccionado) { alert('Selecciona un tecnico'); return; }
    try {
      await api.put(`/averias/${asignarModal.id}/asignar`, { tecnicoId: tecnicoSeleccionado });
      setAsignarModal(null);
      setTecnicoSeleccionado('');
      cargar();
    } catch { alert('Error al asignar tecnico'); }
  };

  const totalAverias   = averias.length;
  const pendientes     = averias.filter(a => a.estado === 'pendiente').length;
  const enProceso      = averias.filter(a => a.estado === 'en_proceso').length;
  const resueltos      = averias.filter(a => a.estado === 'resuelto').length;

  const stats = [
    { label: 'Total',      value: totalAverias, color: '#38bdf8' },
    { label: 'Pendientes', value: pendientes,   color: '#ef4444' },
    { label: 'En proceso', value: enProceso,    color: '#f97316' },
    { label: 'Resueltos',  value: resueltos,    color: '#22c55e' },
  ];

  const tipo = (t: string) => TIPO_BADGE[t] ?? TIPO_BADGE.otro;

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>🔧 {esTecnico ? 'Mis Averías' : 'Averias'}</h2>
          <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>
            {esTecnico ? 'Averías asignadas a ti y pendientes sin asignar' : 'Gestion de averias e incidencias del servicio'}
          </p>
        </div>
        {!esTecnico && (
          <button
            onClick={() => setModal(true)}
            style={{ background:'#ef4444', border:'none', color:'white', padding:'10px 20px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}
          >
            + Reportar Averia
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:10, padding:'14px 18px', border:`1px solid ${s.color}33` }}>
            <div style={{ color:'#64748b', fontSize:12 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:26, fontWeight:800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div>
      ) : averias.length === 0 ? (
        <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>No hay averias registradas</div>
      ) : (
        <div style={{ background:'#1e293b', borderRadius:12, overflow:'hidden', border:'1px solid #334155' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #334155' }}>
                {['Titulo', 'Tipo', 'Estado', 'Cliente', 'Tecnico Asignado', 'Fecha Reporte', 'Acciones'].map(h => (
                  <th key={h} style={{ padding:'12px 14px', textAlign:'left', color:'#64748b', fontSize:12, fontWeight:600, textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {averias.map((a, i) => (
                <tr key={a.id} style={{ borderBottom:'1px solid #1e293b', background: i % 2 === 0 ? '#0f172a' : '#1e293b' }}>
                  <td style={{ padding:'12px 14px', color:'#e2e8f0', fontSize:14, maxWidth:200 }}>
                    <div style={{ fontWeight:600 }}>{a.titulo}</div>
                    {a.descripcion && (
                      <div style={{ color:'#64748b', fontSize:12, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:180 }}>
                        {a.descripcion}
                      </div>
                    )}
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    <span style={{ background: tipo(a.tipo).color + '22', color: tipo(a.tipo).color, borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                      {tipo(a.tipo).label}
                    </span>
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    <span style={{ background: (ESTADO_COLOR[a.estado] ?? '#64748b') + '22', color: ESTADO_COLOR[a.estado] ?? '#64748b', borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                      {a.estado?.replace('_', ' ')}
                    </span>
                  </td>
                  <td style={{ padding:'12px 14px', color:'#94a3b8', fontSize:13 }}>
                    {a.cliente ? `${a.cliente.nombres} ${a.cliente.apellidos}` : <span style={{ color:'#334155' }}>—</span>}
                  </td>
                  <td style={{ padding:'12px 14px', color:'#94a3b8', fontSize:13 }}>
                    {a.tecnicoAsignado
                      ? `${a.tecnicoAsignado.nombre} ${a.tecnicoAsignado.apellido}`
                      : <span style={{ color:'#ef444488', fontSize:12 }}>Sin asignar</span>}
                  </td>
                  <td style={{ padding:'12px 14px', color:'#64748b', fontSize:12 }}>
                    {a.fechaReporte ? new Date(a.fechaReporte).toLocaleDateString('es-PE', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    {esTecnico ? (
                      <div style={{ display:'flex', gap:6 }}>
                        {a.estado === 'pendiente' && (
                          <button onClick={() => marcarEnProceso(a.id)}
                            style={{ background:'#f97316', border:'none', color:'white', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                            🔧 Iniciar
                          </button>
                        )}
                        {a.estado === 'en_proceso' && (
                          <button onClick={() => resolverAveria(a.id)}
                            style={{ background:'#22c55e', border:'none', color:'white', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                            ✅ Resolver
                          </button>
                        )}
                      </div>
                    ) : (
                      a.estado !== 'resuelto' && (
                        <button
                          onClick={() => { setAsignarModal({ id: a.id, titulo: a.titulo }); setTecnicoSeleccionado(a.tecnicoAsignadoId ?? ''); }}
                          style={{ background:'#0ea5e922', border:'1px solid #0ea5e944', color:'#38bdf8', borderRadius:6, padding:'4px 12px', cursor:'pointer', fontSize:12, fontWeight:600 }}>
                          Asignar tecnico
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal crear averia */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'#00000088', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#1e293b', borderRadius:14, padding:28, width:460, border:'1px solid #334155' }}>
            <h3 style={{ color:'#f1f5f9', margin:'0 0 20px', fontSize:18 }}>Reportar nueva averia</h3>

            <label style={{ color:'#94a3b8', fontSize:13, display:'block', marginBottom:4 }}>Titulo *</label>
            <input
              value={form.titulo}
              onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
              placeholder="Ej: Rotura de tuberia en Av. Lima"
              style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#e2e8f0', borderRadius:8, padding:'8px 12px', marginBottom:14, boxSizing:'border-box', fontSize:14 }}
            />

            <label style={{ color:'#94a3b8', fontSize:13, display:'block', marginBottom:4 }}>Tipo</label>
            <select
              value={form.tipo}
              onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
              style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#e2e8f0', borderRadius:8, padding:'8px 12px', marginBottom:14, fontSize:14 }}
            >
              <option value="rotura_tuberia">🔧 Rotura de tuberia</option>
              <option value="fuga_agua">💧 Fuga de agua</option>
              <option value="sin_agua">🚫 Sin agua</option>
              <option value="dano_medidor">📊 Dano en medidor</option>
              <option value="otro">⚠️ Otro</option>
            </select>

            <label style={{ color:'#94a3b8', fontSize:13, display:'block', marginBottom:4 }}>Descripcion</label>
            <textarea
              value={form.descripcion}
              onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
              rows={3}
              placeholder="Descripcion detallada del problema..."
              style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#e2e8f0', borderRadius:8, padding:'8px 12px', marginBottom:20, boxSizing:'border-box', fontSize:14, resize:'vertical' }}
            />

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button
                onClick={() => { setModal(false); setForm({ titulo:'', descripcion:'', tipo:'otro', clienteId:'' }); }}
                style={{ background:'transparent', border:'1px solid #334155', color:'#94a3b8', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontSize:14 }}
              >
                Cancelar
              </button>
              <button
                onClick={crear}
                style={{ background:'#ef4444', border:'none', color:'white', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:700, fontSize:14 }}
              >
                Reportar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal asignar tecnico */}
      {asignarModal && (
        <div style={{ position:'fixed', inset:0, background:'#00000088', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#1e293b', borderRadius:14, padding:28, width:380, border:'1px solid #334155' }}>
            <h3 style={{ color:'#f1f5f9', margin:'0 0 6px', fontSize:18 }}>Asignar tecnico</h3>
            <p style={{ color:'#64748b', fontSize:13, margin:'0 0 20px' }}>{asignarModal.titulo}</p>

            <label style={{ color:'#94a3b8', fontSize:13, display:'block', marginBottom:4 }}>Tecnico</label>
            <select
              value={tecnicoSeleccionado}
              onChange={e => setTecnicoSeleccionado(e.target.value)}
              style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', color:'#e2e8f0', borderRadius:8, padding:'8px 12px', marginBottom:20, fontSize:14 }}
            >
              <option value="">-- Seleccionar tecnico --</option>
              {usuarios.map((u: any) => (
                <option key={u.id} value={u.id}>{u.nombre} {u.apellido}</option>
              ))}
            </select>

            {usuarios.length === 0 && (
              <p style={{ color:'#f97316', fontSize:12, marginTop:-14, marginBottom:14 }}>No hay tecnicos activos registrados.</p>
            )}

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button
                onClick={() => { setAsignarModal(null); setTecnicoSeleccionado(''); }}
                style={{ background:'transparent', border:'1px solid #334155', color:'#94a3b8', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontSize:14 }}
              >
                Cancelar
              </button>
              <button
                onClick={asignar}
                style={{ background:'#0ea5e9', border:'none', color:'white', borderRadius:8, padding:'8px 18px', cursor:'pointer', fontWeight:700, fontSize:14 }}
              >
                Asignar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Averias;
